import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, ShieldOff, Search, RefreshCw, Link2, Code, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const StatusBadge = ({ active }) => (
  active
    ? <Badge className="bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/30">Active</Badge>
    : <Badge className="bg-amber-900/30 text-amber-200 hover:bg-amber-900/30">Disabled</Badge>
);

const Summary = ({ summary }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
    <Card><CardContent className="p-5 text-center"><div className="text-2xl font-bold text-emerald-400">{summary?.shares_active ?? 0}</div><div className="text-xs text-gray-500 mt-1">Active shares</div></CardContent></Card>
    <Card><CardContent className="p-5 text-center"><div className="text-2xl font-bold text-amber-400">{summary?.shares_disabled ?? 0}</div><div className="text-xs text-gray-500 mt-1">Disabled shares</div></CardContent></Card>
    <Card><CardContent className="p-5 text-center"><div className="text-2xl font-bold text-emerald-400">{summary?.embeds_active ?? 0}</div><div className="text-xs text-gray-500 mt-1">Active embeds</div></CardContent></Card>
    <Card><CardContent className="p-5 text-center"><div className="text-2xl font-bold text-amber-400">{summary?.embeds_disabled ?? 0}</div><div className="text-xs text-gray-500 mt-1">Disabled embeds</div></CardContent></Card>
  </div>
);

const ModerationPage = () => {
  const [summary, setSummary] = useState(null);
  const [shares, setShares] = useState([]);
  const [embeds, setEmbeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [disableTarget, setDisableTarget] = useState(null);
  const [disableReason, setDisableReason] = useState('');
  const [notifyOwner, setNotifyOwner] = useState(true);
  const [adminMessage, setAdminMessage] = useState('');
  const [actioning, setActioning] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '50');

      const [sumRes, sRes, eRes, metricsRes] = await Promise.all([
        api.get('/admin/moderation/summary'),
        api.get(`/admin/moderation/shares?${params.toString()}`),
        api.get(`/admin/moderation/embeds?${params.toString()}`),
        // iter166 — grab per-widget impressions/clicks/CTR for the last 30d so
        // moderators see traffic alongside status. Full sort/paging lives on
        // the dedicated Embeds Analytics tab.
        api.get('/admin/embed-analytics/widgets?range=30d&limit=200'),
      ]);
      setSummary(sumRes.data);
      setShares(sRes.data.items || []);
      // Merge embed_analytics metrics into the moderation list by widget id
      const metricsMap = {};
      (metricsRes.data.widgets || []).forEach((w) => { metricsMap[w.id] = w; });
      const enriched = (eRes.data.items || []).map((item) => ({
        ...item,
        _metrics: metricsMap[item.id] || null,
      }));
      setEmbeds(enriched);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load moderation data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const disableItem = async () => {
    if (!disableTarget || disableReason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }
    try {
      setActioning(true);
      const url = disableTarget.kind === 'share'
        ? `/admin/moderation/shares/${disableTarget.id}/disable`
        : `/admin/moderation/embeds/${disableTarget.id}/disable`;
      const r = await api.post(url, {
        reason: disableReason,
        notify_owner: notifyOwner,
        admin_message: adminMessage || undefined,
      });
      const extra = r.data?.owner_notified ? ' & owner notified by email' : '';
      toast.success(`${disableTarget.kind === 'share' ? 'Share' : 'Embed'} disabled${extra}`);
      setDisableTarget(null);
      setDisableReason('');
      setAdminMessage('');
      setNotifyOwner(true);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disable failed');
    } finally {
      setActioning(false);
    }
  };

  const enableItem = async (kind, id) => {
    try {
      const url = kind === 'share'
        ? `/admin/moderation/shares/${id}/enable`
        : `/admin/moderation/embeds/${id}/enable`;
      await api.post(url);
      toast.success(`${kind === 'share' ? 'Share' : 'Embed'} re-enabled`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enable failed');
    }
  };

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[300px]"><Loader2 className="h-8 w-8 animate-spin text-red-400" /></div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-100 mb-2" data-testid="moderation-title">Content Moderation</h1>
          <p className="text-slate-300">Review and force-disable public shares and embed widgets across all users.</p>
        </div>
        <Button variant="outline" onClick={fetchAll} disabled={refreshing} data-testid="refresh-moderation">
          <RefreshCw className={`h-4 w-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Summary summary={summary} />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by owner email or content title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="moderation-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-48" data-testid="moderation-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="disabled">Disabled only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="shares">
        <TabsList className="mb-4">
          <TabsTrigger value="shares" data-testid="tab-shares"><Link2 className="h-4 w-4 mr-1" /> Public Shares ({shares.length})</TabsTrigger>
          <TabsTrigger value="embeds" data-testid="tab-embeds"><Code className="h-4 w-4 mr-1" /> Embed Widgets ({embeds.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="shares">
          <Card>
            <CardContent className="p-0">
              {shares.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">No shares match your filters.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase border-b bg-[#0A1024]">
                      <tr>
                        <th className="text-left py-2 px-3">Owner</th>
                        <th className="text-left py-2 px-3">Content</th>
                        <th className="text-center py-2 px-3">Views</th>
                        <th className="text-left py-2 px-3">Created</th>
                        <th className="text-center py-2 px-3">Status</th>
                        <th className="text-right py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shares.map(s => (
                        <tr key={s.share_token} className="border-b hover:bg-[#0A1024]" data-testid={`share-row-${s.share_token}`}>
                          <td className="py-2 px-3"><div className="font-medium text-slate-100">{s.owner_email}</div></td>
                          <td className="py-2 px-3 max-w-xs truncate">
                            <div className="truncate">{s.content_title}</div>
                            {s.content_url && <a href={s.content_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" /> source</a>}
                          </td>
                          <td className="py-2 px-3 text-center">{s.view_count || 0}</td>
                          <td className="py-2 px-3 text-xs text-slate-300">{fmtDate(s.created_at)}</td>
                          <td className="py-2 px-3 text-center"><StatusBadge active={s.is_active} /></td>
                          <td className="py-2 px-3 text-right">
                            {s.is_active ? (
                              <Button size="sm" variant="outline" className="text-amber-400 border-amber-800/40" onClick={() => setDisableTarget({ kind: 'share', id: s.share_token, label: s.content_title })} data-testid={`disable-share-${s.share_token}`}>
                                <ShieldOff className="h-3 w-3 mr-1" /> Disable
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="text-emerald-300 border-emerald-800/50" onClick={() => enableItem('share', s.share_token)} data-testid={`enable-share-${s.share_token}`}>
                                <ShieldCheck className="h-3 w-3 mr-1" /> Re-enable
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="embeds">
          <Card>
            <CardContent className="p-0">
              {embeds.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-10">No embed widgets match your filters.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-gray-500 uppercase border-b bg-[#0A1024]">
                      <tr>
                        <th className="text-left py-2 px-3">Owner</th>
                        <th className="text-left py-2 px-3">Widget name</th>
                        <th className="text-left py-2 px-3">Theme</th>
                        <th className="text-right py-2 px-3">Impr (30d)</th>
                        <th className="text-right py-2 px-3">Clicks</th>
                        <th className="text-right py-2 px-3">CTR</th>
                        <th className="text-left py-2 px-3">Created</th>
                        <th className="text-center py-2 px-3">Status</th>
                        <th className="text-right py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {embeds.map(w => (
                        <tr key={w.id} className="border-b hover:bg-[#0A1024]" data-testid={`embed-row-${w.id}`}>
                          <td className="py-2 px-3"><div className="font-medium text-slate-100">{w.owner_email}</div></td>
                          <td className="py-2 px-3">{w.name || '(unnamed)'}</td>
                          <td className="py-2 px-3"><Badge variant="outline">{w.theme || '—'}</Badge></td>
                          <td className="py-2 px-3 text-right text-slate-200" data-testid={`embed-impr-${w.id}`}>
                            {(w._metrics?.impressions ?? 0).toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-right text-slate-200" data-testid={`embed-clicks-${w.id}`}>
                            {(w._metrics?.clicks ?? 0).toLocaleString()}
                          </td>
                          <td className="py-2 px-3 text-right" data-testid={`embed-ctr-${w.id}`}>
                            {w._metrics && w._metrics.impressions > 0
                              ? <span className="text-slate-200">{w._metrics.ctr_percent}%</span>
                              : <span className="text-slate-500">—</span>}
                          </td>
                          <td className="py-2 px-3 text-xs text-slate-300">{fmtDate(w.created_at)}</td>
                          <td className="py-2 px-3 text-center"><StatusBadge active={w.is_active} /></td>
                          <td className="py-2 px-3 text-right">
                            {w.is_active ? (
                              <Button size="sm" variant="outline" className="text-amber-400 border-amber-800/40" onClick={() => setDisableTarget({ kind: 'embed', id: w.id, label: w.name || w.id })} data-testid={`disable-embed-${w.id}`}>
                                <ShieldOff className="h-3 w-3 mr-1" /> Disable
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="text-emerald-300 border-emerald-800/50" onClick={() => enableItem('embed', w.id)} data-testid={`enable-embed-${w.id}`}>
                                <ShieldCheck className="h-3 w-3 mr-1" /> Re-enable
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* DISABLE DIALOG */}
      <Dialog open={!!disableTarget} onOpenChange={(open) => { if (!open) { setDisableTarget(null); setDisableReason(''); } }}>
        <DialogContent data-testid="disable-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400"><AlertTriangle className="h-5 w-5" /> Disable {disableTarget?.kind === 'share' ? 'public share' : 'embed widget'}?</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-slate-200">
              You are disabling: <span className="font-medium">{disableTarget?.label}</span>
            </p>
            <div>
              <Label htmlFor="reason">Reason (required, logged to audit trail)</Label>
              <Textarea id="reason" value={disableReason} onChange={(e) => setDisableReason(e.target.value)} placeholder="e.g. Reported as abusive, contains PII, copyright violation…" rows={3} data-testid="disable-reason-input" maxLength={500} />
            </div>
            <div className="flex items-center justify-between gap-3 p-3 border rounded-lg bg-blue-950/40/40">
              <div className="flex-1">
                <Label htmlFor="notify" className="cursor-pointer">Email the owner with this reason</Label>
                <p className="text-xs text-slate-300 mt-0.5">Sends a templated moderation notice from dotbox.ai. Recommended for transparency.</p>
              </div>
              <Switch id="notify" checked={notifyOwner} onCheckedChange={setNotifyOwner} data-testid="notify-owner-toggle" />
            </div>
            {notifyOwner && (
              <div>
                <Label htmlFor="adminMsg">Additional message (optional)</Label>
                <Textarea id="adminMsg" value={adminMessage} onChange={(e) => setAdminMessage(e.target.value)} placeholder="e.g. We'd be happy to re-enable once you remove the flagged content. Reply to this email if you have questions." rows={2} data-testid="admin-message-input" maxLength={500} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableTarget(null)} disabled={actioning}>Cancel</Button>
            <Button variant="destructive" onClick={disableItem} disabled={actioning || disableReason.trim().length < 3} data-testid="confirm-disable-btn">
              {actioning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Disabling…</> : <><ShieldOff className="h-4 w-4 mr-1" /> Disable</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ModerationPage;
